import React from 'react';
import {
  Avatar,
  Box,
  Card,
  CardActionArea,
  Chip,
  Stack,
  Typography,
} from '@mui/material';
import {
  Description as DescriptionIcon,
  Star as StarIcon,
} from '@mui/icons-material';
import { useTheme } from '@mui/material/styles';
import { FormTemplate } from '../types';
import { getTemplateDomainLabel, getTemplateDomainColor, getDifficultyInfo } from '../helpers';
import { getFormTemplateDisplay } from '../../processDisplay';
import { useTranslation } from 'react-i18next';

interface TemplateCardProps {
  template: FormTemplate;
  onOpen: (template: FormTemplate) => void;
}

/**
 * Compact form template card — Apple-style whitespace discipline.
 *
 * Same design grammar as ComponentCard (no shadows, 1px border, hover shift).
 * Adds: difficulty chip, star icon for Popular.
 */
const TemplateCard: React.FC<TemplateCardProps> = ({ template, onOpen }) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const display = getFormTemplateDisplay(t, template);
  const isPopular = !!template.isPopular;
  const categoryColor = getTemplateDomainColor(theme, template.category);
  const difficulty = getDifficultyInfo(t, theme, template.difficulty);

  return (
    <Card
      variant="outlined"
      sx={{
        height: '100%',
        width: '100%',
        borderRadius: 2,
        borderColor: isPopular ? `${theme.palette.warning.main}80` : 'divider',
        transition: 'border-color 120ms ease',
        '&:hover': {
          borderColor: theme.palette.primary.main,
        },
      }}
    >
      <CardActionArea
        onClick={() => onOpen(template)}
        sx={{ height: '100%', p: 2 }}
      >
        <Stack direction="row" spacing={1.5} alignItems="flex-start">
          <Avatar
            sx={{
              width: 36,
              height: 36,
              bgcolor: categoryColor,
              color: theme.palette.getContrastText(categoryColor),
              flexShrink: 0,
              '& svg': { fontSize: '1.125rem' },
            }}
          >
            {template.icon || <DescriptionIcon />}
          </Avatar>
          <Box sx={{ flexGrow: 1, minWidth: 0 }}>
            <Stack direction="row" alignItems="center" spacing={0.75}>
              <Typography
                variant="subtitle2"
                sx={{
                  fontWeight: 600,
                  fontSize: '0.9rem',
                  lineHeight: 1.3,
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                  flex: 1,
                  minWidth: 0,
                }}
                title={display.name}
              >
                {display.name}
              </Typography>
              {isPopular && (
                <StarIcon
                  sx={{ fontSize: 14, color: theme.palette.warning.main, flexShrink: 0 }}
                  titleAccess={t('processes:formComponents.popular')}
                />
              )}
            </Stack>
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{
                display: '-webkit-box',
                WebkitLineClamp: 2,
                WebkitBoxOrient: 'vertical',
                overflow: 'hidden',
                lineHeight: 1.4,
                mt: 0.25,
              }}
            >
              {display.description}
            </Typography>
            <Stack direction="row" spacing={0.5} sx={{ mt: 1, flexWrap: 'wrap', gap: 0.5 }}>
              <Chip
                label={getTemplateDomainLabel(t, template.category)}
                size="small"
                sx={{
                  height: 18,
                  fontSize: '0.65rem',
                  bgcolor: `${categoryColor}20`,
                  color: categoryColor,
                  fontWeight: 500,
                  border: 'none',
                }}
              />
              <Chip
                label={difficulty.label}
                size="small"
                variant="outlined"
                sx={{
                  height: 18,
                  fontSize: '0.65rem',
                  borderColor: difficulty.color,
                  color: difficulty.color,
                  fontWeight: 500,
                }}
              />
              {template.isSystem && (
                <Chip
                  label={t('processes:formComponents.dialog.system')}
                  size="small"
                  variant="outlined"
                  sx={{
                    height: 18,
                    fontSize: '0.65rem',
                    borderColor: theme.palette.info.main,
                    color: theme.palette.info.main,
                    fontWeight: 600,
                  }}
                />
              )}
              <Chip
                label={t('processes:liveDisplay.ui.fieldsCount', { count: template.items.length })}
                size="small"
                variant="outlined"
                sx={{
                  height: 18,
                  fontSize: '0.65rem',
                  borderColor: theme.palette.divider,
                  color: 'text.secondary',
                }}
              />
            </Stack>
          </Box>
        </Stack>
      </CardActionArea>
    </Card>
  );
};

export default TemplateCard;
